import { Component, inject, Input, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { LatLng } from "leaflet";
import { AuthService } from "src/app/auth.service";
import { bohrpunktNummerLabel, idOf, isB, istPunktartDrehbar, istPunktartMitNummer, istPunktartMitProtokoll, ProjectAssignment, punktTemplate } from "src/app/data-model";
import { ProjectDocument } from "src/app/project.service";
import { TransformationService, WmsDataService, StorageService, GpsService, RecentValuesService } from '@luzula/luzula-lib';
import { ProjectService } from "src/app/project.service";
import { WeatherService } from '@luzula/luzula-lib';
import { first } from 'rxjs/operators';
import { merkePunktart, zuletztGewaehltePunktart } from './punktart-favoriten';
import { verfahrenVon } from '../verfahren/verfahren';
import {
	AbschnittId, UEBERNAHME_WAHL_KEY, abschnitteFuer, gemerkteWahl, hatAbschnittInhalt, uebernimmAbschnitte, zeilenBehalten,
} from '../uebernahme/punkt-abschnitte';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-neu-modal',
    templateUrl: './bohrpunkt-neu.component.html',
})
export class BohrpunktNeuComponent implements OnInit {
	activeModal = inject(NgbActiveModal);

	@Input() bpTemplate?: any;
	@Input() position?: LatLng;
	bp!: any;

	/**
	 * Der Punkt, aus dem übernommen werden kann (das offene Protokoll). Er wird NICHT
	 * mehr kopiert: Nutzer-Entscheidung 28.09.2026, übernommen wird nur, was angehakt
	 * ist (PLAN-punktdaten-uebernehmen.md).
	 */
	vorlage?: any;
	/** Die angehakten Abschnitte; beim Anlegen gemerkt für den nächsten Punkt. */
	gewaehlt: AbschnittId[] = [];
	/** Vorlage und Projekt sind SEA 95 - nur dann gibt es die Abschnitte. */
	uebernahmeMoeglich = false;

	isB = isB;
	istPunktartMitProtokoll = istPunktartMitProtokoll;
	istPunktartMitNummer = istPunktartMitNummer;
	istPunktartDrehbar = istPunktartDrehbar;
	bohrpunktNummerLabel = bohrpunktNummerLabel;

	private ps = inject(ProjectService);
	private as = inject(AuthService);
	private storage = inject(StorageService);
	private ts = inject(TransformationService);
	private wms_data = inject(WmsDataService);
	private weatherService = inject(WeatherService);
	private gps = inject(GpsService);
	private recentValues = inject(RecentValuesService);

	ngOnInit(): void {
		// Prüfen ob Projekt aktiv - Punkt-Erstellung nur bei offenem Projekt
		if (!this.ps.project()) {
			this.activeModal.dismiss('no-project');
			return;
		}

		const useTemplate = !!this.bpTemplate;
		if (useTemplate) {
			// Ein LEERER Punkt, nicht die Kopie der Vorlage. Bis zum 28.09.2026 wurde die
			// Vorlage ganz kopiert; dabei wanderten Adresse, Aufnahmedatum und
			// Labornummer mit, und die Adresse wurde am neuen Ort nie geholt (die App
			// fragt sie nur ab, wenn das Feld leer ist). Punktart und Pfeilrichtung
			// bleiben vorgewählt - beides steht sichtbar im Fenster.
			this.vorlage = this.bpTemplate;
			this.bp = punktTemplate(this.bpTemplate.type);
			if (typeof this.bpTemplate.rotation === 'number') this.bp.rotation = this.bpTemplate.rotation;
			this.gewaehlt = gemerkteWahl(this.storage.getSync(UEBERNAHME_WAHL_KEY));
			void this.pruefeUebernahme();
		}
		else {
			this.bp = punktTemplate();
		}

		if (!useTemplate) {
			this.bp.type = this.vorbelegtePunktart() || "6";
		}
		// Rotation passend zur (ggf. vorbelegten) Punktart initialisieren.
		this.onTypeChange(this.bp.type);

		// Aktuelles Projekt zuweisen
		this.assignCurrentProject();

		this.as.user().subscribe((user: any) => {
			this.bp.header.aufnahme.kartierer = user?.name;
		});

		if (this.position) {
			this.applyPosition(this.position);
		}
		else {
			// GpsService respektiert die aktive Quelle (Serial-COM bevorzugt gegenüber
			// navigator.geolocation), damit der gesetzte Punkt zu der Position passt,
			// die die Karte anzeigt. navigator.geolocation würde auf Windows-Tablets
			// mit COM-Port-GPS die ungenaue Windows-Location-API-Position liefern.
			const last = this.gps.lastPosition;
			if (last) {
				this.applyPosition(last);
			} else {
				this.gps.position().pipe(first()).subscribe(pos => this.applyPosition(pos));
			}
		}
	}

	private applyPosition(latlng: LatLng): void {
		const pt = this.ts.toEPSG5650(latlng);
		this.bp.header.koordinaten.x = pt.x;
		this.bp.header.koordinaten.y = pt.y;
		this.wms_data.fetchWmsDataForDocument(this.bp);
		this.fetchCurrentWeather(latlng);
	}

	private async fetchCurrentWeather(latlng: LatLng) {
		try {
			const weather = await this.weatherService.getCurrentWeather(latlng.lat, latlng.lng);
			if (weather) {
				const description = this.weatherService.describeWeather(weather);
				this.bp.extendedHeader.witterung = description.full;
			}
		} catch (error) {
			console.warn('[BohrpunktNeuComponent] Fehler beim Abrufen der Witterung:', error);
		}
	}
	
	/**
	 * Punktart geändert: bei drehbaren Symbolen die Rotation vorbelegen
	 * (0° = Osten, wie gezeichnet), sonst das Feld wieder entfernen, damit
	 * nicht-drehbare Punkte kein verwaistes rotation-Feld behalten.
	 */
	onTypeChange(type: string): void {
		this.bp.type = type;
		if (istPunktartDrehbar(type)) {
			if (typeof this.bp.rotation !== 'number') this.bp.rotation = 0;
		} else {
			delete this.bp.rotation;
		}
	}

	onClose(redirect: boolean) {
		// Der Punkt entsteht jetzt — erst damit ist die Punktart gewaehlt. Ein
		// abgebrochenes Anlegen zaehlt nicht (das Fenster merkt sich nichts).
		merkePunktart(this.recentValues, this.storage, this.bp.type);
		// Defensiv: kein rotation-Feld an nicht-drehbaren Punkten speichern.
		if (!istPunktartDrehbar(this.bp.type)) {
			delete this.bp.rotation;
		}
		if (istPunktartMitProtokoll(this.bp.type)) {
			this.bp.Horizontfolge = [];
			this.bp.Substratfolge = [];
			this.bp.bodenvegetation = { /*bestandsBeschreibung: '', */aufnahme: { schichtAnteile: {}, vegetationen: [] } };
			this.bp.extendedHeader.relief = {};
			if (idOf(this.bp.type) === 7) {
				this.bp.extendedHeader.entwaesserungsverhalten = 'II';
				this.bp.extendedHeader.entwaesserungVorschlag = 'II';
			}
		}
		if (!istPunktartMitNummer(this.bp.type)) {
			delete this.bp.header.nummer;
		}
		// Erst die Vorgaben oben, dann die angehakten Abschnitte: Was man aus dem
		// Vorlage-Punkt übernimmt, gewinnt (z.B. das Entwässerungsverhalten eines Moorpunkts).
		const abschnitte = this.abschnitteZumUebernehmen();
		if (abschnitte.length > 0) uebernimmAbschnitte(this.bp, this.vorlage, abschnitte);
		// Gemerkt wird die Wahl, wie sie im Fenster stand - auch wenn diese Punktart
		// einen Abschnitt nicht kennt: Beim nächsten Punkt kann er wieder gelten.
		if (this.vorlage && this.uebernahmeMoeglich) {
			this.storage.setSync(UEBERNAHME_WAHL_KEY, this.gewaehlt);
		}
		// `zeilen`: Roberts Schalter in `createBohrpunktFrom` - ohne sie würden die
		// übernommenen Zeilen beim Anlegen wieder geleert.
		this.activeModal.close({bp: this.bp, redirect: redirect, zeilen: zeilenBehalten(abschnitte)});
	}

	/** Überschrift der Hakenliste, mit der Nummer des Vorlage-Punktes, wenn er eine hat. */
	vorlageTitel(): string {
		const nummer = String(this.vorlage?.header?.nummer ?? '').trim();
		return nummer ? `Aus Punkt ${nummer} übernehmen` : 'Aus dem offenen Punkt übernehmen';
	}

	/** Zeigt das Fenster die Abschnitte zum Übernehmen? */
	zeigeUebernahme(): boolean {
		return !!this.vorlage && this.uebernahmeMoeglich && istPunktartMitProtokoll(this.bp?.type);
	}

	/**
	 * Was beim Anlegen wirklich übernommen wird: die angehakten Abschnitte, die es bei
	 * der gewählten Punktart gibt und in denen die Vorlage etwas stehen hat.
	 */
	abschnitteZumUebernehmen(): AbschnittId[] {
		if (!this.zeigeUebernahme()) return [];
		const moeglich = new Set(abschnitteFuer(this.bp.type).map(a => a.id));
		return this.gewaehlt.filter(id => moeglich.has(id) && hatAbschnittInhalt(this.vorlage, id));
	}

	/**
	 * Nur zwischen Aufnahmen nach SEA 95: Die Abschnitte sind die Felder dieses Bogens.
	 * Das Projekt zählt mit, weil ES bestimmt, was der neue Punkt wird
	 * (`richtePunktNachProjektAus`) - in einem KA-Projekt landeten SEA-Angaben in einem
	 * Punkt, der sie nie zeigt.
	 */
	private async pruefeUebernahme(): Promise<void> {
		if (verfahrenVon(this.vorlage) !== 'sea95') {
			this.uebernahmeMoeglich = false;
			return;
		}
		try {
			const verfahren = await this.ps.getProjectValue('verfahren');
			this.uebernahmeMoeglich = verfahrenVon({ verfahren }) === 'sea95';
		} catch {
			this.uebernahmeMoeglich = false;
		}
	}

	private async assignCurrentProject(): Promise<void> {
		const projectId = this.ps.project();
		if (!projectId) return;

		const projectDoc = await this.ps.getOne(projectId) as ProjectDocument | undefined;
		const projectName = projectDoc?.name || projectId;

		if (!this.bp.header.projects) {
			this.bp.header.projects = [];
		}

		// Nur hinzufügen wenn nicht bereits zugewiesen
		const alreadyAssigned = this.bp.header.projects.some(
			(p: ProjectAssignment) => p.id === projectId
		);
		if (!alreadyAssigned) {
			this.bp.header.projects.push({ id: projectId, name: projectName });
		}
	}

	/**
	 * Die ZULETZT gewaehlte Punktart — die Vorbelegung beim Anlegen.
	 *
	 * Bewusst nicht die haeufigste: Oben im Auswahl-Fenster stehen die haeufigsten,
	 * vorbelegt wird die letzte Wahl (Nutzer-Entscheidung 19.09.2026). Wer eine
	 * Reihe gleichartiger Punkte setzt, bekommt so beim naechsten Punkt wieder
	 * das, womit er gerade arbeitet. Leer (frisches Geraet, nur entfallene
	 * Punktarten gemerkt) heisst "keine" — der Aufrufer nimmt dann die Vorgabe.
	 */
	private vorbelegtePunktart(): string {
		return zuletztGewaehltePunktart(this.storage);
	}
}
